<template>
  <div>
    <div
      v-if="isValidationMessageDisplaying"
      class="mb-2 tag-validation-banner p-1"
    >
      <RsIcon
        class="icon-color-error"
        icon="circle-exclamation"
        weight="fas"
      />
      Tag already exists!
    </div>
    <label
      v-if="label"
      :for="id"
    >{{ label }}<sup v-if="required">*</sup></label>
    <VoerroTagsInput
      :id="id"
      v-model="tagValue"
      :placeholder="placeholder"
      :typeahead="typeahead"
      :limit="limit"
      :add-tags-on-space="addTagsOnSpace"
      :add-tags-on-comma="addTagsOnComma"
      :add-tags-on-blur="addTagsOnBlur"
      :disabled="disabled"
      :validate="validateDuplicates"
      :input-id="inputId"
      :allow-duplicates="true"
      @tags-updated="onUpdate()"
      @keyup="onSearch($event.composedPath()[0])"
      @blur="isValidOnBlur"
      @focus="onFocus"
      @tag-added="onTagAdded"
      @tag-removed="onTagRemoved"
      @keydown="onKeydown"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import VoerroTagsInput from '@voerro/vue-tagsinput';

type IVoerroTagsInputVal = { key: string, value: string }[];

export default defineComponent({

  name: 'RsFormTagsInput',
  components: { VoerroTagsInput },

  props: {
    id: {
      type: String,
      default: 'rs-form-tags-input',
    },

    label: {
      type: String,
    },

    required: {
      type: Boolean,
      default: false,
    },

    disabled: {
      type: Boolean,
      default: false,
    },

    typeahead: {
      type: Boolean,
      default: false,
    },

    limit: {
      type: Number,
      default: 0,
    },

    addTagsOnSpace: {
      type: Boolean,
      default: false,
    },

    addTagsOnComma: {
      type: Boolean,
      default: false,
    },

    addTagsOnBlur: {
      type: Boolean,
      default: false,
    },

    placeholder: {
      type: String,
    },

    readonly: {
      type: String,
    },

    validate: {
      type: Function as PropType<(val: IVoerroTagsInputVal[0] | string) => boolean>,
    },

    value: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      tagValue: [] as IVoerroTagsInputVal,
      inputId: `vue-tagsinput-input-${this.id}` as string,
      isValidationMessageDisplaying: false as boolean,
    };
  },

  watch: {
    value: {
      deep: true,
      immediate: true,
      handler(newVal: string) {
        this.tagValue = (newVal) ? newVal.split(',').map((tag: string) => ({ key: '', value: tag })) : [];
      },
    },
  },

  methods: {
    onUpdate() {
      return this.tagValue.map((tag) => tag.value).join(',');
    },

    onTagAdded(tag: IVoerroTagsInputVal[0]) {
      this.onInput(tag, 'add');
      this.$emit('tag-added', tag);
      return tag;
    },

    onTagRemoved(tag: IVoerroTagsInputVal[0]) {
      this.onInput(tag, 'remove');
      this.$emit('tag-removed', tag);
      return tag;
    },

    onInput(tag: IVoerroTagsInputVal[0], type: 'remove' | 'add'): string {
      if (type === 'add') {
        if (!this.value) {
          this.$emit('input', tag.value);
          return tag.value;
        }

        if (this.value.split(',').includes(tag.value)) {
          this.$emit('input', this.value);
          return this.value;
        }
        const value = [...this.value.split(','), tag.value].join(',');
        this.$emit('input', value);
        return value;
      }

      const value = this.value.split(',')
        .filter((eml: string) => eml !== tag.value)
        .join(',');

      this.$emit('input', value);
      return value;
    },

    onKeydown(event: Event) {
      if (this.limit && this.tagValue.length >= this.limit) {
        event.preventDefault();
      }
    },

    isValidOnBlur() {
      if (!this.validate) {
        this.$emit('is-valid-input-on-blur', true);
        return true;
      }

      const input: any = document.getElementById(this.inputId);

      if (!input) {
        this.$emit('is-valid-input-on-blur', true);
        return true;
      }

      if (!input.value) {
        this.$emit('is-valid-input-on-blur', true);
        return true;
      }

      this.$emit('is-valid-input-on-blur', this.validate(input.value));
      return this.validate(input.value);
    },

    onFocus() {
      this.$emit('on-focus');
    },

    onSearch(input: HTMLInputElement) {
      this.$emit('on-search', input.value);
      return input.value;
    },

    validateDuplicates(tag: string) {
      this.isValidationMessageDisplaying = false;
      if (this.tagValue.find((t) => t.value === tag)) {
        this.isValidationMessageDisplaying = true;

        this.$emit('on-validate', false);
        return false;
      }

      const validateResult = this.validate ? this.validate(tag) : true;
      this.$emit('on-validate', validateResult);
      return validateResult;
    },
  },
});
</script>

<style lang="scss">
@import '@/styles/_variables.scss';

/* The input */
.tags-input {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
}

.tags-input input {
  flex: 1;
  background: transparent;
  border: none;
}

.tags-input input:focus {
  outline: none;
}

.tags-input input[type="text"] {
  color: #495057;
}

.tags-input-wrapper-default {
  padding: .5em .25em;

  background: #fff;

  border: 1px solid transparent;
  border-radius: .25em;
  border-color: #dbdbdb;
}

.tags-input-wrapper-default.active {
  border: 1px solid #8bbafe;
  box-shadow: 0 0 0 0.2em rgba(13, 110, 253, 0.25);
  outline: 0 none;
}

/* The tag badges & the remove icon */
.tags-input span {
  margin: 0.3em 0.3em;
}

.tags-input-remove {
  cursor: pointer;
  position: absolute;
  display: inline-block;
  right: 0.3em;
  top: 0.3em;
  padding: 0.5em;
  overflow: hidden;
}

.tags-input-remove:focus {
  outline: none;
}

.tags-input-remove:before, .tags-input-remove:after {
  content: '';
  position: absolute;
  width: 75%;
  left: 0.15em;
  background: #fff;
  height: 2px;
  margin-top: -1px;
}

.tags-input-remove:before {
  transform: rotate(45deg);
}
.tags-input-remove:after {
  transform: rotate(-45deg);
}

/* Tag badge styles */
.tags-input-badge {
  position: relative;
  display: inline-block;
  padding: 0.35em 0.4em;
  font-size: 75%;
  font-weight: 700;
  line-height: 1;
  text-align: center;
  white-space: nowrap;
  vertical-align: baseline;
  border-radius: 0.25em;
  overflow: hidden;
  text-overflow: ellipsis;
}

.tags-input-badge-pill {
  padding-right: 1.25em;
  padding-left: 0.6em;
  border-radius: 0.4em;
}
.tags-input-badge-pill.disabled {
  padding-right: 0.6em;
}

.tags-input-badge-selected-default {
  color: #ffff;
  background-color: #7d59e1;
}

/* Typeahead */
.typeahead-hide-btn {
  color: #999 !important;
  font-style: italic;
}

/* Typeahead - badges */
.typeahead-badges > span {
  margin-top: .5em;
}

.typeahead-badges > span {
  cursor: pointer;
  margin-right: 0.3em;
}

/* Typeahead - dropdown */
.typeahead-dropdown {
  list-style-type: none;
  padding: 0;
  margin: 0;
  position: absolute;
  width: 100%;
  z-index: 1000;
}

.typeahead-dropdown li {
  padding: .25em 1em;
  cursor: pointer;
}

/* Typeahead elements style/theme */
.tags-input-typeahead-item-default {
  color: #fff;
  background-color: #343a40;
}

.tags-input-typeahead-item-highlighted-default {
  color: #fff;
  background-color: #007bff !important;
}

.tag-validation-banner {
  color: $global-text-color;
  justify-content: space-evenly;
  background-color: $error-light;
  border: 1px solid $error-dark;
  border-radius: $input-radius;
  width: fit-content;

  .icon-color-error {
    color: $error-dark;
  }
}
</style>
